// Extraction-status badge logic for unmatched faxes in the admin fax inbox
// (AdminPage FaxClassificationBadge). Pure — kept out of AdminPage.js so it
// can be unit-tested without mounting the page.

// Backend stamps these on file_classification_cache rows when a fax has been
// retired without text. OCR_Failed_Memory specifically means the OCR was
// killed by Render's OOM killer (see backend/api/pending_fax_recovery.py:
// mark_processing_started + _looks_like_silent_kill). Surface them with a
// distinct red badge instead of the regular teal "Category" pill.
export const FAX_FAILURE_CLASSIFICATIONS = {
  OCR_Failed_Memory: "Failed to extract — low Render memory",
  OCR_Failed_Permanent: "Failed to extract text",
};
export const isFaxFailureClassification = (c) =>
  Boolean(c) && Object.prototype.hasOwnProperty.call(FAX_FAILURE_CLASSIFICATIONS, c);
export const faxFailureLabel = (c) => FAX_FAILURE_CLASSIFICATIONS[c] || "Failed to extract";

// Mirrors backend/api/pending_fax_recovery.py:OOM_INFERRED_ERROR_MARKER. When
// the backend stamps this string on last_processing_error, we KNOW the
// previous attempt died silently (the only thing that produces that exact
// marker is mark_processing_started detecting the kill signature).
export const OOM_INFERRED_ERROR_MARKER =
  "previous_attempt_killed_silently_likely_oom";
// Mirrors backend MAX_PROCESSING_ATTEMPTS / STUCK_THRESHOLD_MINUTES so we can
// derive an extraction-status label without round-tripping to the backend.
export const FAX_MAX_PROCESSING_ATTEMPTS = 5;
export const FAX_STUCK_THRESHOLD_MS = 5 * 60 * 1000;
// Mirrors backend/api/pending_fax_recovery.py FAX_QUEUED_AT_KEY /
// FAX_QUEUED_GRACE_MINUTES. The SRFax webhook stamps metadata.fax_queued_at
// when it queues a fax on the bounded (2-worker) fax pool; until an attempt
// starts (processing_attempts 0/null) the fax is waiting its turn, not dead.
export const FAX_QUEUED_AT_KEY = "fax_queued_at";
export const FAX_QUEUED_GRACE_MS = 30 * 60 * 1000;

// ms since the webhook queued this fax, or null when it carries no (valid)
// queued marker.
const _faxQueuedAgeMs = (fax) => {
  const ts = fax?.metadata?.[FAX_QUEUED_AT_KEY];
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return null;
  return Date.now() - t;
};

const _faxRowAgeMs = (fax) => {
  const ts = fax?.updated_at || fax?.created_at;
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return null;
  return Date.now() - t;
};

// Compute a user-facing extraction-status descriptor for an unmatched fax that
// hasn't yielded any text yet. Returns null if the row looks healthy enough
// that no status badge is warranted.
//
// Logic order (most specific → most generic):
//   1. Backend already retired the row as OCR_Failed_* → friendly label.
//   2. Backend stamped OOM marker → friendly memory label.
//   3. Real failure reason on the row → "Could not extract text" + tooltip.
//   4. Reached MAX attempts without a stamped reason → generic failure.
//   5. No attempt yet and queued by the webhook < 30 min ago → "Queued".
//   6. Stuck > 5 min with no error → infer OOM (matches backend heuristic;
//      handles old rows that pre-date the marker).
//   7. Otherwise → still processing (with attempt count if any).
export const _computeExtractionStatus = (fax) => {
  if (!fax) return null;

  if (isFaxFailureClassification(fax.classification)) {
    return {
      kind: "failed",
      label: faxFailureLabel(fax.classification),
      tooltip:
        fax.last_processing_error || `classification: ${fax.classification}`,
    };
  }

  const attempts = Number(fax.processing_attempts) || 0;
  const err = fax.last_processing_error || "";
  const ageMs = _faxRowAgeMs(fax);
  const isStuck = ageMs !== null && ageMs > FAX_STUCK_THRESHOLD_MS;

  if (err === OOM_INFERRED_ERROR_MARKER) {
    return {
      kind: "failed",
      label: "Failed to extract — low Render memory",
      tooltip:
        "Backend detected a silent kill on the OCR worker (OOM signature).",
    };
  }
  if (err) {
    return {
      kind: "warning",
      label: "Could not extract text",
      tooltip: err,
    };
  }
  if (attempts >= FAX_MAX_PROCESSING_ATTEMPTS) {
    return { kind: "failed", label: "Failed to extract text" };
  }
  if (attempts > 0 && isStuck) {
    return {
      kind: "failed",
      label: "Likely failed — low Render memory",
      tooltip:
        "OCR worker was incremented but never stamped a result. Most often this is an OOM kill on Render — the process dies before it can log anything.",
    };
  }
  if (attempts > 0) {
    return {
      kind: "processing",
      label: `Extracting text... (attempt ${attempts}/${FAX_MAX_PROCESSING_ATTEMPTS})`,
    };
  }
  const queuedAgeMs = _faxQueuedAgeMs(fax);
  if (queuedAgeMs !== null && queuedAgeMs < FAX_QUEUED_GRACE_MS) {
    // attempts=0/null and the webhook queued it < 30 min ago: it is waiting
    // behind other faxes on the fax pool, not OOM-killed.
    return {
      kind: "processing",
      label: "Queued",
      tooltip:
        "Waiting for a free fax worker — faxes are OCR'd a couple at a time. " +
        "Text extraction starts automatically.",
    };
  }
  if (isStuck) {
    // attempts=0/null AND stuck → either pre-fix orphan (NULL attempts that the
    // sweep used to skip) or a brand-new row whose OCR thread died before the
    // pre-emptive increment. Treat as a likely OOM since that's the dominant
    // cause of pending faxes that never start.
    return {
      kind: "failed",
      label: "Likely failed — low Render memory",
      tooltip:
        "Row has been pending OCR with no progress for several minutes. " +
        "Most likely the OCR worker was OOM-killed before it could record an attempt.",
    };
  }
  return { kind: "processing", label: "Extracting text..." };
};
